// GtJobFamilies: the New job families library (generativetalent.co/New-job-families) for the team.
//
// /job-families             every family by group: written, flagged, live, downloads; write and stop
// /job-families/:familyId    one family: its intro and every role, to read, edit, rewrite with a
//                            note, and publish or unpublish (the family or one role at a time)
// /job-families/agent-workforce   a collection: its roles from many families, section by section (C1-JFCOLL2)
// ?role=R0109                opens that role on its family page (from the role search on the list)
//
// Publishing warns and never blocks, except trainer or human data wording, which never goes live.
// C1-JOBFAM2 (30 Oct 2026)
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthFetch } from './GtLexaAdmin';
import gtConfirm from '../../components/GtConfirm';

const STATUS = { hiring_now: 'Hiring now', emerging: 'Emerging', scenario: 'Scenario' };
const ROLE_FIELDS = [
  ['summary', 'Summary', 'The line under the title, and what search engines and Beth read first.'],
  ['does', 'What they do', ''],
  ['delivers', 'What a strong hire delivers', ''],
  ['strong_hire', 'How you can tell they are good', ''],
  ['hard_to_fill', 'Why it is hard to fill', ''],
  ['find_them', 'Where to find them', ''],
  ['seo', 'Search description', 'What Google shows under the link. 120 to 155 characters.'],
];
const FAMILY_FIELDS = [
  ['intro', 'Introduction', 'Under the family name on its page and on its card.'],
  ['why_now', 'Why now', ''],
  ['seo', 'Search description', '120 to 155 characters.'],
];
const FILTERS = [['all', 'All'], ['unwritten', 'Not written'], ['flagged', 'Worth a look'], ['ready', 'Written, not live'], ['live', 'Live']];

function ukTime(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; }
}
function famState(f) {
  if (f.waiting > 0 || ['queued', 'writing'].includes(f.copy_status)) return { label: 'Writing', tone: 'blue' };
  if (f.published && f.published_roles > 0) return f.published_roles < f.roles ? { label: 'Partly live', tone: 'green' } : { label: 'Live', tone: 'green' };
  if (f.written === f.roles && f.roles > 0) return { label: 'Written', tone: 'amber' };
  if (f.written > 0) return { label: f.written + ' of ' + f.roles + ' written', tone: 'amber' };
  return { label: 'Not written', tone: 'slate' };
}
function roleState(r) {
  if (['queued', 'writing'].includes(r.copy_status)) return { label: 'Writing', tone: 'blue' };
  if (r.copy_status === 'failed') return { label: 'Writing failed', tone: 'rose' };
  if (r.published) return { label: 'Live', tone: 'green' };
  if (['draft', 'edited'].includes(r.copy_status)) return { label: r.copy_status === 'edited' ? 'Edited' : 'Written', tone: 'amber' };
  return { label: 'Not written', tone: 'slate' };
}
function money(v, cur) { return (cur === 'GBP' ? '£' : '$') + Math.round((Number(v) || 0) / 1000) + 'k'; }
const PAY_STATE = { none: 'Not researched', queued: 'Researching', working: 'Researching', done: 'Benchmarked', empty: 'Not enough evidence', failed: 'Research failed' };

// C1-JOBFAM4: indicative pay for one role: what was found, where from, hide or correct it.
function PayBox({ r, authFetch, onChanged }) {
  const [busy, setBusy] = useState('');
  const [edit, setEdit] = useState(null);
  const [msg, setMsg] = useState('');
  if (r.status === 'scenario') return <div className="gtjf-pay"><span className="gtiv-k">Indicative pay</span><p className="gtivl-muted">Scenario roles carry no pay.</p></div>;
  const p = r.pay || {};
  async function call(url, body, method) {
    setBusy(url); setMsg('');
    try { await authFetch(url, { method: method || 'POST', body: JSON.stringify(body || {}) }); setEdit(null); await onChanged(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  const market = (m, label) => {
    const x = p[m];
    const hidden = !!p['hidden_' + m];
    return (
      <div className={'gtjf-paym' + (hidden ? ' gtjf-paym--off' : '')} key={m}>
        <span className="gtjf-paym-h"><b>{label}</b>{x && <label className="gtjf-hide"><input type="checkbox" checked={!hidden} onChange={(e) => call('/api/job-families/admin/roles/' + r.id + '/pay', { ['hidden_' + m]: !e.target.checked }, 'PATCH')} /> Show on the site</label>}</span>
        {edit && edit.m === m ? (
          <span className="gtjf-payedit">
            <input className="gtiv-in" inputMode="numeric" value={edit.low} onChange={(e) => setEdit(Object.assign({}, edit, { low: e.target.value.replace(/[^\d]/g, '') }))} aria-label="Low" />
            <span>to</span>
            <input className="gtiv-in" inputMode="numeric" value={edit.high} onChange={(e) => setEdit(Object.assign({}, edit, { high: e.target.value.replace(/[^\d]/g, '') }))} aria-label="High" />
            <button type="button" className="gt-btn" onClick={() => call('/api/job-families/admin/roles/' + r.id + '/pay', { [m]: { low: Number(edit.low), high: Number(edit.high) } }, 'PATCH')}>Save</button>
            <button type="button" className="gtiv-link" onClick={() => setEdit(null)}>Cancel</button>
          </span>
        ) : x ? (
          <span className="gtjf-payv"><strong>{money(x.low, x.currency)} to {money(x.high, x.currency)}</strong><em>{x.sources ? x.sources.length : 0} sources{x.edited_by ? ' · corrected by ' + x.edited_by : ''}</em><button type="button" className="gtiv-link" onClick={() => setEdit({ m, low: String(x.low), high: String(x.high) })}>Correct</button></span>
        ) : (
          <span className="gtjf-payv"><em>No range</em><button type="button" className="gtiv-link" onClick={() => setEdit({ m, low: '', high: '' })}>Add one by hand</button></span>
        )}
        {x && x.sources && x.sources.length > 0 && (
          <ul className="gtjf-paysrc">{x.sources.map((s, i) => <li key={i}><a className="gtiv-link" href={s.url} target="_blank" rel="noopener noreferrer">{s.title || s.url}</a><q>{s.quote}</q></li>)}</ul>
        )}
      </div>
    );
  };
  return (
    <div className="gtjf-pay">
      <span className="gtjf-paytop"><span className="gtiv-k">Indicative pay</span><span className="gtjf-pill gtjf-pill--slate">{PAY_STATE[r.pay_status] || r.pay_status}</span>
        <button type="button" className="gt-btn" disabled={!!busy || ['queued', 'working'].includes(r.pay_status)} onClick={() => call('/api/job-families/admin/pay', { scope: 'role', role_id: r.id })}>{r.pay_status === 'none' ? 'Research pay' : 'Research again'}</button></span>
      {r.pay_status === 'failed' && <p className="gtiv-err gtjf-msg">{r.pay_error}</p>}
      {r.pay_status === 'empty' && <p className="gtivl-muted">The published evidence was not strong enough for a range. The page says pay is benchmarked as part of a search.</p>}
      {(r.pay || r.pay_status === 'done' || r.pay_status === 'empty') && <div className="gtjf-paygrid">{market('uk', 'United Kingdom (GBP)')}{market('us', 'United States (USD)')}</div>}
      {msg && <p className="gtjf-msg">{msg}</p>}
    </div>
  );
}

// C1-JOBFAM3: who downloaded what.
function Downloads({ authFetch, navigate }) {
  const [rows, setRows] = useState(null);
  const [next, setNext] = useState(null);
  useEffect(() => { authFetch('/api/job-families/admin/leads').then((x) => { setRows(x.leads); setNext(x.next); }).catch(() => setRows([])); }, [authFetch]);
  async function more() { const x = await authFetch('/api/job-families/admin/leads?before=' + next); setRows((o) => o.concat(x.leads)); setNext(x.next); }
  if (!rows || !rows.length) return null;
  return (
    <section className="gtjf-leads">
      <h2>Downloads</h2>
      <div className="gtivl-list">
        {rows.map((l) => (
          <button key={l.id} type="button" className="gtiv-card gtivl-row gtjf-lead" onClick={() => l.client_id && navigate('/clients/' + l.client_id)}>
            <span className="gtivl-row-t"><b>{l.name}{l.company ? ', ' + l.company : ''}</b><span>{l.kind === 'kit' ? 'Role Kit: ' + (l.role_title || '') : 'The whole library (CSV)'} · {l.email}</span><span>{ukTime(l.created_at)}{l.downloads > 1 ? ' · opened ' + l.downloads + ' times' : ''}</span></span>
            <span className={'gtjf-pill gtjf-pill--' + (l.kind === 'kit' ? 'green' : 'blue')}>{l.kind === 'kit' ? 'Role Kit' : 'Library'}</span>
          </button>
        ))}
        {next && <button type="button" className="gtc-btn-ghost" onClick={more}>Show 50 more</button>}
      </div>
    </section>
  );
}

function Pill({ s }) { return <span className={'gtjf-pill gtjf-pill--' + s.tone}>{s.label}</span>; }
function Flags({ flags }) {
  if (!flags || !flags.length) return null;
  return (
    <ul className="gtjf-flags">
      {flags.map((f, i) => <li key={i} className={f.code === 'trainer' ? 'gtjf-flag--stop' : ''}><b>{f.field.replace('_', ' ')}</b> {f.text}</li>)}
    </ul>
  );
}
function Area({ value, onChange, rows, label, hint, flags, field }) {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = Math.max(el.scrollHeight, 44) + 'px'; } }, [value]);
  const mine = (flags || []).filter((f) => f.field === field);
  const words = String(value || '').split(/\s+/).filter(Boolean).length;
  return (
    <label className={'gtjf-field' + (mine.length ? ' gtjf-field--flag' : '')}>
      <span className="gtjf-field-h"><b>{label}</b><em>{field === 'seo' ? String(value || '').length + ' characters' : words + ' words'}</em></span>
      {hint && <span className="gtjf-hint">{hint}</span>}
      <textarea ref={ref} className="gtiv-in gtjf-ta" rows={rows || 2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      {mine.length > 0 && <span className="gtjf-fieldflag">{mine.map((f) => f.text).join(' · ')}</span>}
    </label>
  );
}

// ---------------------------------------------------------------- the list
function FamilyList({ authFetch, navigate }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(() => authFetch('/api/job-families/admin').then((x) => { setD(x); setError(''); }).catch((e) => setError(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const writing = d && d.progress && (d.progress.writing || d.progress.paying);
  useEffect(() => { if (!writing) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [writing, load]);

  async function write(scope) {
    if (scope === 'all' && !(await gtConfirm({ title: 'Rewrite everything?', message: ['Every family and role that a person has not edited is written again from scratch. Edited ones are kept.', 'Live pages keep showing the current words until each new version is written.'], confirmLabel: 'Rewrite everything' }))) return;
    setBusy(scope);
    try { await authFetch('/api/job-families/admin/write', { method: 'POST', body: JSON.stringify({ scope }) }); await load(); } catch (e) { setError(e.message); } finally { setBusy(''); }
  }
  async function researchPay() {
    setBusy('pay');
    try { await authFetch('/api/job-families/admin/pay', { method: 'POST', body: JSON.stringify({ scope: 'missing' }) }); await load(); } catch (e) { setError(e.message); } finally { setBusy(''); }
  }
  async function stop() {
    setBusy('stop');
    try { await authFetch('/api/job-families/admin/stop', { method: 'POST', body: '{}' }); await load(); } catch (e) { setError(e.message); } finally { setBusy(''); }
  }

  // C1-JFCOLL2: search reads role titles and their other titles as well as family names.
  const roleHits = useMemo(() => {
    const words = q.trim().toLowerCase();
    if (!d || !d.roles || words.length < 2) return [];
    const famName = new Map(d.families.map((f) => [f.id, f.name]));
    return d.roles.filter((r) => [r.title].concat(r.aliases || []).some((t) => String(t).toLowerCase().indexOf(words) >= 0))
      .map((r) => Object.assign({}, r, { family_name: famName.get(r.family_id) || '' }));
  }, [d, q]);
  const fams = useMemo(() => {
    if (!d) return [];
    const words = q.trim().toLowerCase();
    const hitFams = new Set(roleHits.map((r) => r.family_id));
    return d.families.filter((f) => {
      if (words && f.name.toLowerCase().indexOf(words) < 0 && !hitFams.has(f.id)) return false;
      if (filter === 'unwritten') return f.written < f.roles;
      if (filter === 'flagged') return f.flagged > 0 || f.failed > 0 || f.flag_count > 0;
      if (filter === 'ready') return f.written > 0 && f.published_roles < f.written;
      if (filter === 'live') return f.published && f.published_roles > 0;
      return true;
    });
  }, [d, filter, q, roleHits]);

  const p = d ? d.progress : null;
  const pct = p && p.roles ? Math.round((p.roles_written / p.roles) * 100) : 0;
  return (
    <div className="gtc-wrap gtjf">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Job families</h1>
          <p className="gtc-sub">The New job families library on generativetalent.co. Write it, read it, put it live.</p>
        </div>
        <div className="gtjf-actions">
          {d && <a className="gt-btn" href={d.site + d.base} target="_blank" rel="noopener noreferrer">Open the library &#8599;</a>}
          {writing
            ? <button type="button" className="gt-btn" disabled={busy === 'stop'} onClick={stop}>Stop writing</button>
            : <button type="button" className="gt-btn" disabled={!!busy} onClick={() => write('all')}>Rewrite everything</button>}
          {p && p.paying
            ? <button type="button" className="gt-btn" disabled>Researching pay…</button>
            : <button type="button" className="gt-btn" disabled={!!busy || (p && p.pay_done + p.pay_empty >= p.pay_roles)} onClick={() => researchPay()}>{p && p.pay_done + p.pay_empty === 0 ? 'Research pay' : 'Research missing pay'}</button>}
          <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || (p && p.roles_written === p.roles && p.families_written === p.families)} onClick={() => write('missing')}>
            {busy === 'missing' ? 'Starting…' : p && p.roles_written === 0 ? 'Write all' : 'Write what is missing'}
          </button>
        </div>
      </div>
      {error && <div className="gtiv-card gtiv-err">{error} <button type="button" className="gtiv-link" onClick={load}>Try again</button></div>}

      <section className="gtiv-card gtjf-top">
        <div className="gtlx-stats">
          <div><b>{p ? p.roles_written + ' / ' + p.roles : '–'}</b><span>roles written</span></div>
          <div><b>{p ? p.roles_flagged : '–'}</b><span>worth a look</span></div>
          <div><b>{p ? p.roles_published : '–'}</b><span>roles live</span></div>
          <div><b>{p ? p.families_published : '–'}</b><span>families live</span></div>
          <div><b>{p ? p.pay_done + ' / ' + p.pay_roles : '–'}</b><span>pay benchmarked</span></div>
          <div><b>{p && p.downloads != null ? p.downloads : '–'}</b><span>downloads this month</span></div>
        </div>
        {p && (writing || p.roles_written < p.roles) && (
          <div className="gtjf-prog">
            <div className="gtjf-bar"><i style={{ width: pct + '%' }} /></div>
            <span>{p.paying && !p.writing ? 'Researching pay: ' + p.pay_waiting + ' roles to go.' : writing ? 'Writing now: ' + (p.roles_waiting + p.families_waiting) + ' to go. You can leave this screen; it carries on.' : (p.roles - p.roles_written) + ' roles not written yet.'}{p.roles_failed ? ' ' + p.roles_failed + ' failed and will be tried again with Write what is missing.' : ''}</span>
          </div>
        )}
      </section>

      <div className="gtjf-bar2">
        <div className="gtivl-filters" role="tablist">
          {FILTERS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={filter === k} className={'gtivl-filter' + (filter === k ? ' gtivl-filter--on' : '')} onClick={() => setFilter(k)}>{l}</button>)}
        </div>
        <input className="gtiv-in gtjf-search" type="search" placeholder="Search roles, other titles and families" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search roles, other titles and families" />
      </div>

      {roleHits.length > 0 && (
        <section className="gtjf-group gtjf-hits">
          <h2>Roles<span>{roleHits.length} {roleHits.length === 1 ? 'match' : 'matches'}</span></h2>
          <div className="gtivl-list">
            {roleHits.slice(0, 40).map((r) => (
              <button key={r.id} type="button" className="gtiv-card gtivl-row gtjf-hit" onClick={() => navigate('/job-families/' + r.family_id + '?role=' + r.id)}>
                <span className="gtivl-row-t"><b>{r.title}</b><span>{r.family_name} · {STATUS[r.status]}{r.flag_count > 0 ? ' · ' + r.flag_count + (r.flag_count === 1 ? ' flag' : ' flags') : ''}</span></span>
                <Pill s={roleState(r)} />
              </button>
            ))}
            {roleHits.length > 40 && <p className="gtivl-muted">Showing 40 of {roleHits.length}. Add a word to narrow it down.</p>}
          </div>
        </section>
      )}

      {d && !q.trim() && filter === 'all' && (d.collections || []).map((c) => (
        <button key={c.slug} type="button" className="gtiv-card gtjf-coll" onClick={() => navigate('/job-families/' + c.slug)}>
          <span className="gtjf-coll-l">Collection</span>
          <span className="gtjf-coll-t"><b>{c.name}</b><Pill s={c.published_roles >= c.roles ? { label: 'Live', tone: 'green' } : c.published_roles > 0 ? { label: 'Partly live', tone: 'green' } : { label: 'Not live', tone: 'slate' }} /></span>
          <span className="gtjf-coll-s">{c.strap}</span>
          <span className="gtjf-fam-m">{c.roles} roles from across the library · {c.written} written · {c.published_roles} live{c.flagged ? ' · ' + c.flagged + ' worth a look' : ''}</span>
          <span className="gtjf-minibar" aria-hidden="true"><i className="gtjf-mb-live" style={{ flex: c.published_roles }} /><i className="gtjf-mb-written" style={{ flex: Math.max(0, c.written - c.published_roles) }} /><i className="gtjf-mb-none" style={{ flex: Math.max(0, c.roles - c.written) }} /></span>
        </button>
      ))}

      {!d ? <div className="gtiv-card gtiv-loading">Loading job families…</div> : fams.length === 0 ? (
        <div className="gtiv-card gtivl-empty"><b>Nothing matches</b><span>Try another filter.</span></div>
      ) : d.groups.map((g) => {
        const list = fams.filter((f) => f.group_id === g.id);
        if (!list.length) return null;
        return (
          <section key={g.id} className="gtjf-group">
            <h2>{g.name}<span>{list.length} {list.length === 1 ? 'family' : 'families'}</span></h2>
            <div className="gtjf-grid">
              {list.map((f) => {
                const s = famState(f);
                return (
                  <button key={f.id} type="button" className="gtiv-card gtjf-fam" onClick={() => navigate('/job-families/' + f.id)}>
                    <span className="gtjf-fam-t"><b>{f.name}</b><Pill s={s} /></span>
                    <span className="gtjf-fam-m">{f.roles} roles · {f.written} written · {f.published_roles} live{f.downloads ? ' · ' + f.downloads + (f.downloads === 1 ? ' download' : ' downloads') : ''}</span>
                    <span className="gtjf-minibar" aria-hidden="true"><i className="gtjf-mb-live" style={{ flex: f.published_roles }} /><i className="gtjf-mb-written" style={{ flex: Math.max(0, f.written - f.published_roles) }} /><i className="gtjf-mb-none" style={{ flex: Math.max(0, f.roles - f.written) }} /></span>
                    {(f.flagged > 0 || f.failed > 0) && <span className="gtjf-fam-f">{f.flagged > 0 ? f.flagged + ' worth a look' : ''}{f.flagged > 0 && f.failed > 0 ? ' · ' : ''}{f.failed > 0 ? f.failed + ' failed' : ''}</span>}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
      <Downloads authFetch={authFetch} navigate={navigate} />
    </div>
  );
}

// ---------------------------------------------------------------- one role
function RoleCard({ r, authFetch, siteBase, onChanged, open, onToggle, showFamily }) {
  const [copy, setCopy] = useState(r.copy || {});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { if (!dirty) setCopy(r.copy || {}); }, [r.copy, dirty]);
  const s = roleState(r);
  const set = (k, v) => { setCopy((c) => Object.assign({}, c, { [k]: v })); setDirty(true); };
  const jd = copy.jd || {};
  const setJd = (k, v) => set('jd', Object.assign({}, jd, { [k]: v }));
  const qs = Array.isArray(copy.questions) ? copy.questions : [];
  const setQ = (i, k, v) => set('questions', qs.map((x, n) => (n === i ? Object.assign({}, x, { [k]: v }) : x)));
  const lines = (a) => (Array.isArray(a) ? a.join('\n') : '');
  const unlines = (t) => String(t || '').split('\n').map((x) => x.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);

  async function save() {
    setBusy('save'); setMsg('');
    try { await authFetch('/api/job-families/admin/roles/' + r.id, { method: 'PATCH', body: JSON.stringify({ copy }) }); setDirty(false); setMsg('Saved.'); await onChanged(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function rewrite() {
    if (dirty && !(await gtConfirm({ title: 'Rewrite this role?', message: 'Your unsaved changes will be lost.', confirmLabel: 'Rewrite' }))) return;
    setBusy('rewrite'); setMsg('');
    try { await authFetch('/api/job-families/admin/roles/' + r.id + '/rewrite', { method: 'POST', body: JSON.stringify({ note: note.trim() || null }) }); setDirty(false); setNote(''); setMsg('Being rewritten. It takes about a minute.'); await onChanged(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function publish(on) {
    setBusy('pub'); setMsg('');
    try { await authFetch('/api/job-families/admin/roles/' + r.id + '/publish', { method: 'POST', body: JSON.stringify({ published: on }) }); await onChanged(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  const written = ['draft', 'edited'].includes(r.copy_status);
  const flags = r.copy_flags || [];
  return (
    <div id={'jfr-' + r.id} className={'gtiv-card gtjf-role' + (open ? ' gtjf-role--open' : '')}>
      <button type="button" className="gtjf-role-h" onClick={onToggle} aria-expanded={open}>
        <span className="gtjf-role-t"><b>{r.title}</b><em>{STATUS[r.status]}{showFamily && r.family_name ? ' · ' + r.family_name : ''}{r.origin === 'recruiter' ? ' · from the recruiter file' : ''}</em></span>
        {flags.length > 0 && <span className={'gtjf-flagcount' + (flags.some((f) => f.code === 'trainer') ? ' gtjf-flagcount--stop' : '')}>{flags.length} {flags.length === 1 ? 'flag' : 'flags'}</span>}
        {r.downloads > 0 && <span className="gtjf-dl">{r.downloads} {r.downloads === 1 ? 'download' : 'downloads'}</span>}
        <Pill s={s} />
        <span className="gtjf-chev" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="gtjf-role-b">
          {r.copy_status === 'failed' && <p className="gtiv-err gtjf-msg">Writing failed: {r.copy_error}. Rewrite to try again.</p>}
          {!written && r.copy_status !== 'failed' && !['queued', 'writing'].includes(r.copy_status) && <p className="gtivl-muted">Not written yet. Rewrite to write it now, or use Write what is missing on the list.</p>}
          {['queued', 'writing'].includes(r.copy_status) && <p className="gtivl-muted">Being written now.</p>}
          <Flags flags={flags} />
          {written && (
            <div className="gtjf-form">
              {ROLE_FIELDS.map(([k, l, h]) => <Area key={k} field={k} label={l} hint={h} value={copy[k]} onChange={(v) => set(k, v)} flags={flags} rows={k === 'summary' || k === 'seo' ? 2 : 4} />)}
              <div className="gtjf-sub"><b>Interview questions</b><span>Two show on the page. All of them go in the Role Kit download.</span></div>
              {qs.map((x, i) => (
                <div key={i} className="gtjf-q">
                  <Area field={'q' + i} label={'Question ' + (i + 1)} value={x.q} onChange={(v) => setQ(i, 'q', v)} rows={2} />
                  <Area field={'g' + i} label="What a good answer covers" value={x.good} onChange={(v) => setQ(i, 'good', v)} rows={2} />
                </div>
              ))}
              <div className="gtjf-sub"><b>Job description</b><span>Ready to post. One point per line.</span></div>
              <Area field="jd_intro" label="Opening" value={jd.intro} onChange={(v) => setJd('intro', v)} rows={3} />
              <Area field="jd_resp" label="What you will do" value={lines(jd.responsibilities)} onChange={(v) => setJd('responsibilities', unlines(v))} rows={5} />
              <Area field="jd_req" label="What you will bring" value={lines(jd.requirements)} onChange={(v) => setJd('requirements', unlines(v))} rows={5} />
              <Area field="jd_nice" label="Nice to have" value={lines(jd.nice_to_have)} onChange={(v) => setJd('nice_to_have', unlines(v))} rows={3} />
            </div>
          )}
          <PayBox r={r} authFetch={authFetch} onChanged={onChanged} />
          <div className="gtjf-facts">
            <span className="gtiv-k">The facts it was written from</span>
            <dl>
              {r.scope && <><dt>What it is</dt><dd>{r.scope}</dd></>}
              {r.deliverable && <><dt>Deliverable</dt><dd>{r.deliverable}</dd></>}
              {r.success_measure && <><dt>Success measure</dt><dd>{r.success_measure}</dd></>}
              {r.recruiter_note && <><dt>Recruiter note</dt><dd>{r.recruiter_note}</dd></>}
              {r.background_note && <><dt>Background</dt><dd>{r.background_note}</dd></>}
              {r.aliases && r.aliases.length > 0 && <><dt>Also called</dt><dd>{r.aliases.join(', ')}</dd></>}
            </dl>
          </div>
          <div className="gtjf-bar3">
            <input className="gtiv-in gtjf-note" placeholder="Note for a rewrite (optional), for example: more about the security side" value={note} onChange={(e) => setNote(e.target.value)} />
            <button type="button" className="gt-btn" disabled={!!busy || ['queued', 'writing'].includes(r.copy_status)} onClick={rewrite}>{busy === 'rewrite' ? 'Starting…' : 'Rewrite'}</button>
            {written && <button type="button" className="gt-btn" disabled={!dirty || !!busy} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save changes'}</button>}
            {r.published
              ? <button type="button" className="gt-btn" disabled={!!busy} onClick={() => publish(false)}>Take offline</button>
              : <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || !written || dirty} onClick={() => publish(true)} title={dirty ? 'Save your changes first' : !written ? 'Write it first' : ''}>Put live</button>}
            {r.published && <a className="gtiv-link" href={siteBase + '/' + r.slug} target="_blank" rel="noopener noreferrer">View on the site &#8599;</a>}
          </div>
          {msg && <p className="gtjf-msg">{msg}</p>}
          {r.copy_at && <p className="gtjf-meta">{r.copy_by === 'writer' ? 'Written' : 'Edited by ' + r.copy_by} {ukTime(r.copy_at)}{r.copy_note ? ' · note: ' + r.copy_note : ''}</p>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- one family
function FamilyPage({ id, authFetch, navigate }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [copy, setCopy] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => authFetch('/api/job-families/admin/families/' + encodeURIComponent(id)).then((x) => { setD(x); setError(''); return x; }).catch((e) => setError(e.message)), [authFetch, id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (d && d.family && !dirty) setCopy(d.family.copy || {}); }, [d, dirty]);
  const busyRole = (r) => ['queued', 'writing'].includes(r.copy_status) || ['queued', 'working'].includes(r.pay_status);
  const writing = d && (d.collection ? d.sections.some((x) => x.roles.some(busyRole))
    : (['queued', 'writing'].includes(d.family.copy_status) || d.roles.some(busyRole)));
  // C1-JFCOLL2: arriving from the role search opens that role and scrolls to it, once.
  const [sp] = useSearchParams();
  const wantRole = sp.get('role');
  const opened = useRef(false);
  useEffect(() => {
    if (!d || !wantRole || opened.current) return;
    opened.current = true;
    setOpen((o) => Object.assign({}, o, { [wantRole]: true }));
    setTimeout(() => { const el = document.getElementById('jfr-' + wantRole); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 60);
  }, [d, wantRole]);
  useEffect(() => { if (!writing) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [writing, load]);

  if (error && !d) return <div className="gtc-wrap gtjf"><div className="gtiv-card gtiv-err">{error}</div></div>;
  if (!d) return <div className="gtc-wrap gtjf"><div className="gtiv-card gtiv-loading">Loading…</div></div>;
  if (d.collection) {
    const all = [].concat(...d.sections.map((x) => x.roles));
    const cw = all.filter((r) => ['draft', 'edited'].includes(r.copy_status)).length;
    const cl = all.filter((r) => r.published).length;
    return (
      <div className="gtc-wrap gtjf">
        <button type="button" className="gtjf-back" onClick={() => navigate('/job-families')}>&larr; Job families</button>
        <div className="gtc-head">
          <div>
            <h1 className="gtc-title">{d.collection.name} <span className="gtjf-pill gtjf-pill--blue">Collection</span></h1>
            <p className="gtc-sub">{d.collection.strap} {all.length} roles · {cw} written · {cl} live. Each role is edited and put live in its own family; changes here show there too.</p>
          </div>
          <div className="gtjf-actions">
            <a className="gt-btn" href={d.site + d.collection.path} target="_blank" rel="noopener noreferrer">View on the site &#8599;</a>
          </div>
        </div>
        {writing && <div className="gtiv-card gtjf-banner gtjf-banner--blue">Writing now. This page updates by itself.</div>}
        {d.sections.map((x) => (
          <section key={x.name} className="gtjf-csec">
            <div className="gtjf-rolesh"><h2>{x.name}</h2><span className="gtivl-muted">{x.roles.length} {x.roles.length === 1 ? 'role' : 'roles'}</span></div>
            <p className="gtivl-muted gtjf-csec-b">{x.blurb}</p>
            <div className="gtjf-roles">
              {x.roles.map((r) => (
                <RoleCard key={r.id} r={r} authFetch={authFetch} siteBase={d.site + d.base + '/' + r.family_slug} onChanged={load} showFamily
                  open={!!open[r.id]} onToggle={() => setOpen((o) => Object.assign({}, o, { [r.id]: !o[r.id] }))} />
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }
  const f = d.family;
  const siteBase = d.site + d.base + '/' + f.slug;
  const written = d.roles.filter((r) => ['draft', 'edited'].includes(r.copy_status));
  const live = d.roles.filter((r) => r.published);
  const famWritten = ['draft', 'edited'].includes(f.copy_status);
  const s = famState({ waiting: writing ? 1 : 0, copy_status: f.copy_status, published: f.published, published_roles: live.length, roles: d.roles.length, written: written.length });

  async function act(kind) {
    setBusy(kind); setMsg('');
    try {
      if (kind === 'write') await authFetch('/api/job-families/admin/write', { method: 'POST', body: JSON.stringify({ scope: 'family', family_id: f.id, note: note.trim() || null, keep_edited: true }) });
      if (kind === 'pay') { const x = await authFetch('/api/job-families/admin/pay', { method: 'POST', body: JSON.stringify({ scope: 'family', family_id: f.id }) }); setMsg('Researching pay for ' + x.queued + ' roles. It takes a few minutes.'); }
      if (kind === 'rewrite') await authFetch('/api/job-families/admin/families/' + f.id + '/rewrite', { method: 'POST', body: JSON.stringify({ note: note.trim() || null }) });
      if (kind === 'save') { await authFetch('/api/job-families/admin/families/' + f.id, { method: 'PATCH', body: JSON.stringify({ copy }) }); setDirty(false); setMsg('Saved.'); }
      if (kind === 'publish') {
        const flagged = written.filter((r) => (r.copy_flags || []).length > 0).length;
        if (flagged && !(await gtConfirm({ title: 'Put this family live?', message: [flagged + (flagged === 1 ? ' role has' : ' roles have') + ' flags worth a look. They go live too, unless a flag is trainer or human data wording, which never goes live.', (d.roles.length - written.length) ? (d.roles.length - written.length) + ' roles are not written yet and stay offline.' : ''], confirmLabel: 'Put it live' }))) { setBusy(''); return; }
        const r = await authFetch('/api/job-families/admin/families/' + f.id + '/publish', { method: 'POST', body: JSON.stringify({ published: true }) });
        setMsg('Live: ' + r.roles_published + ' roles.' + (r.skipped.length ? ' Not live: ' + r.skipped.map((x) => x.title + ' (' + x.why.toLowerCase() + ')').join(', ') + '.' : '') + ' The site shows it within five minutes.');
      }
      if (kind === 'unpublish') {
        if (!(await gtConfirm({ title: 'Take this family offline?', message: 'The family and every role in it come off the site within five minutes. The words are kept.', confirmLabel: 'Take it offline', tone: 'danger' }))) { setBusy(''); return; }
        await authFetch('/api/job-families/admin/families/' + f.id + '/publish', { method: 'POST', body: JSON.stringify({ published: false }) });
        setMsg('Taken offline.');
      }
      setNote('');
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }

  return (
    <div className="gtc-wrap gtjf">
      <button type="button" className="gtjf-back" onClick={() => navigate('/job-families')}>&larr; Job families</button>
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">{f.name} <Pill s={s} /></h1>
          <p className="gtc-sub">{f.group_name} · {d.roles.length} roles · {written.length} written · {live.length} live</p>
        </div>
        <div className="gtjf-actions">
          {f.published && <a className="gt-btn" href={siteBase} target="_blank" rel="noopener noreferrer">View on the site &#8599;</a>}
          {f.published
            ? <button type="button" className="gt-btn" disabled={!!busy} onClick={() => act('unpublish')}>Take offline</button>
            : null}
          <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || !written.length || writing} onClick={() => act('publish')} title={!written.length ? 'Write the roles first' : ''}>
            {f.published ? 'Put the rest live' : 'Put this family live'}
          </button>
        </div>
      </div>
      {msg && <div className="gtiv-card gtjf-banner">{msg}</div>}
      {writing && <div className="gtiv-card gtjf-banner gtjf-banner--blue">Writing now. This page updates by itself.</div>}

      <section className="gtiv-card gtjf-famcopy">
        <div className="gtjf-sub"><b>The family</b><span>{f.scope}</span></div>
        <Flags flags={f.copy_flags} />
        {famWritten
          ? FAMILY_FIELDS.map(([k, l, h]) => <Area key={k} field={k} label={l} hint={h} value={copy[k]} onChange={(v) => { setCopy((c) => Object.assign({}, c, { [k]: v })); setDirty(true); }} flags={f.copy_flags} rows={k === 'why_now' ? 4 : 2} />)
          : <p className="gtivl-muted">{['queued', 'writing'].includes(f.copy_status) ? 'Being written now.' : f.copy_status === 'failed' ? 'Writing failed: ' + f.copy_error : 'Not written yet.'}</p>}
        <div className="gtjf-bar3">
          <input className="gtiv-in gtjf-note" placeholder="Note for writing (optional), applies to the family and its roles" value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className="gt-btn" disabled={!!busy || writing} onClick={() => act('write')}>Write the family and its roles</button>
          {famWritten && <button type="button" className="gt-btn" disabled={!!busy || writing} onClick={() => act('rewrite')}>Rewrite the intro only</button>}
          <button type="button" className="gt-btn" disabled={!!busy} onClick={() => act('pay')}>Research pay for this family</button>
          {famWritten && <button type="button" className="gt-btn" disabled={!dirty || !!busy} onClick={() => act('save')}>Save changes</button>}
        </div>
      </section>

      <div className="gtjf-rolesh">
        <h2>Roles</h2>
        <button type="button" className="gtiv-link" onClick={() => setOpen(d.roles.reduce((a, r) => Object.assign(a, { [r.id]: !Object.values(open).some(Boolean) }), {}))}>{Object.values(open).some(Boolean) ? 'Close all' : 'Open all'}</button>
      </div>
      <div className="gtjf-roles">
        {d.roles.map((r) => (
          <RoleCard key={r.id} r={r} authFetch={authFetch} siteBase={siteBase} onChanged={load} open={!!open[r.id]} onToggle={() => setOpen((o) => Object.assign({}, o, { [r.id]: !o[r.id] }))} />
        ))}
      </div>
    </div>
  );
}

export default function GtJobFamilies({ getToken }) {
  const authFetch = useAuthFetch(getToken);
  const navigate = useNavigate();
  const { familyId } = useParams();
  return familyId
    ? <FamilyPage key={familyId} id={familyId} authFetch={authFetch} navigate={navigate} />
    : <FamilyList authFetch={authFetch} navigate={navigate} />;
}
